import React, { useContext, useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container, Button, Form, Dropdown, NavDropdown } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import projectService from '../services/projectService';
import userService from '../services/userService';
import reportService from '../services/reportService';

const AppNavbar = () => {
  const { isAuthenticated, currentUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ projects: [], users: [], reports: [] });
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Global search handler
  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery.trim() || !isAuthenticated) {
        setSearchResults({ projects: [], users: [], reports: [] });
        setShowResults(false);
        return;
      }
      
      setIsSearching(true);
      setShowResults(true);
      
      try {
        const searchLower = searchQuery.toLowerCase();
        
        // Search projects
        let projects = [];
        if (currentUser?.role === 'admin' || currentUser?.role === 'manager') {
          const allProjects = await projectService.getAllProjects();
          projects = allProjects.filter(p => 
            p.name.toLowerCase().includes(searchLower) ||
            (p.description && p.description.toLowerCase().includes(searchLower))
          ).slice(0, 5);
        }
        
        // Search users (admin only)
        let users = [];
        if (currentUser?.role === 'admin' || currentUser?.role === 'manager') {
          const allUsers = await userService.getAllUsers();
          users = allUsers.filter(u => 
            u.name.toLowerCase().includes(searchLower) ||
            u.email.toLowerCase().includes(searchLower)
          ).slice(0, 5);
        }
        
        // Search reports
        let reports = [];
        if (currentUser?.role === 'admin' || currentUser?.role === 'manager') {
          const reportsResponse = await reportService.getAllReports({}, 1, 50); // Get first 50 reports
          reports = (reportsResponse.reports || []).filter(r => 
            r.project_name?.toLowerCase().includes(searchLower) ||
            r.user_name?.toLowerCase().includes(searchLower)
          ).slice(0, 5);
        }
        
        setSearchResults({ projects, users, reports });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };
    
    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, isAuthenticated, currentUser]);
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleResultClick = (type, id, name) => {
    setShowResults(false);
    const query = searchQuery;
    setSearchQuery('');
    
    if (type === 'project') {
      // Navigate to project hours with the project pre-selected
      navigate('/admin/project-hours', { state: { selectedProjectId: id, searchTerm: name } });
    } else if (type === 'user') {
      // Navigate to users management with search pre-filled
      navigate('/admin/users', { state: { searchTerm: name } });
    } else if (type === 'report') {
      // Navigate to reports with search pre-filled
      navigate('/admin/reports', { state: { searchTerm: name } });
    }
  };
  
  const getTotalResults = () => {
    return searchResults.projects.length + searchResults.users.length + searchResults.reports.length;
  };

  return (
    <Navbar expand="lg" fixed="top" className="mb-0 py-2" style={{
      background: 'linear-gradient(135deg, #7C6FA0 0%, #9B8FBF 50%, #6BA3C7 100%)',
      borderBottom: 'none',
      zIndex: 1000
    }}>
      <Container fluid style={{ paddingLeft: '2rem', paddingRight: '2rem' }}>
        <Navbar.Brand as={Link} to="/" style={{ fontWeight: 700, letterSpacing: '-0.3px', fontSize: '1.2rem' }}>
          <span style={{ opacity: 0.9 }}>Daily Report</span>
        </Navbar.Brand>
        <div className="d-flex align-items-center flex-grow-1 justify-content-between">
          <Nav className="d-flex flex-row align-items-center">
            {isAuthenticated && (
              <>
                {currentUser?.role === 'admin' || currentUser?.role === 'manager' ? (
                  <>
                    <Nav.Link as={Link} to="/admin/dashboard" className="px-3">Dashboard</Nav.Link>
                    <Nav.Link as={Link} to="/admin/reports" className="px-3">All Reports</Nav.Link>
                    <Nav.Link as={Link} to="/admin/projects" className="px-3">Manage Projects</Nav.Link>
                    <Nav.Link as={Link} to="/admin/project-hours" className="px-3">All Projects</Nav.Link>
                    <Nav.Link as={Link} to="/admin/users" className="px-3">Manage Users</Nav.Link>
                    <Nav.Link as={Link} to="/admin/register" className="px-3">Register User</Nav.Link>
                    <Nav.Link as={Link} to="/tasks" className="px-3">Task Management</Nav.Link>
                  </>
                ) : (
                  <>
                    <Nav.Link as={Link} to="/dashboard" className="px-3">Dashboard</Nav.Link>
                    <Nav.Link as={Link} to="/reports/new" className="px-3">Submit Report</Nav.Link>
                    <Nav.Link as={Link} to="/reports" className="px-3">My Reports</Nav.Link>
                    <Nav.Link as={Link} to="/tasks" className="px-3">My Tasks</Nav.Link>
                  </>
                )}
              </>
            )}
          </Nav>
          
          {/* Global Search */}
          {isAuthenticated && (currentUser?.role === 'admin' || currentUser?.role === 'manager') && (
            <div className="position-relative me-3" ref={searchRef} style={{ minWidth: '300px' }}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search everything..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowResults(true)}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    borderRadius: 10,
                    padding: '0.6rem 2.5rem 0.6rem 1rem',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s ease',
                    width: '300px'
                  }}
                  className="search-input"
                />
                <i className="bi bi-search" style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.7)',
                  pointerEvents: 'none',
                  zIndex: 5
                }}></i>
              </div>
              
              {showResults && searchQuery && (
                <div 
                  className="bg-white shadow-lg rounded"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1050,
                    border: '1px solid #dee2e6'
                  }}
                >
                  {isSearching ? (
                    <div className="p-3 text-center text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Searching...
                    </div>
                  ) : getTotalResults() === 0 ? (
                    <div className="p-3 text-center text-muted">
                      <i className="bi bi-inbox" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                      <p className="mb-0 mt-2">No results found</p>
                    </div>
                  ) : (
                    <>
                      {searchResults.projects.length > 0 && (
                        <div className="border-bottom">
                          <div className="px-3 py-2 bg-light">
                            <small className="text-muted fw-semibold">PROJECTS</small>
                          </div>
                          {searchResults.projects.map(project => (
                            <div
                              key={project.id}
                              className="px-3 py-2 cursor-pointer"
                              style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                              onClick={() => handleResultClick('project', project.id, project.name)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <div className="d-flex align-items-center">
                                <i className="bi bi-folder me-2" style={{ color: '#7C6FA0' }}></i>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{project.name}</div>
                                  {project.description && (
                                    <small className="text-muted">{project.description.substring(0, 50)}...</small>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {searchResults.users.length > 0 && (
                        <div className="border-bottom">
                          <div className="px-3 py-2 bg-light">
                            <small className="text-muted fw-semibold">USERS</small>
                          </div>
                          {searchResults.users.map(user => (
                            <div
                              key={user.id}
                              className="px-3 py-2 cursor-pointer"
                              style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                              onClick={() => handleResultClick('user', user.id, user.name)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <div className="d-flex align-items-center">
                                <i className="bi bi-person-circle me-2" style={{ color: '#6BA3C7' }}></i>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{user.name}</div>
                                  <small className="text-muted">{user.email}</small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {searchResults.reports.length > 0 && (
                        <div>
                          <div className="px-3 py-2 bg-light">
                            <small className="text-muted fw-semibold">REPORTS</small>
                          </div>
                          {searchResults.reports.map(report => (
                            <div
                              key={report.id}
                              className="px-3 py-2 cursor-pointer"
                              style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                              onClick={() => handleResultClick('report', report.id, report.project_name || report.user_name)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <div className="d-flex align-items-center">
                                <i className="bi bi-file-text me-2" style={{ color: '#28a745' }}></i>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{report.project_name}</div>
                                  <small className="text-muted">by {report.user_name}</small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          
          <Nav className="align-items-center">
            {isAuthenticated ? (
              <>
                <NavDropdown 
                  title={
                    <span style={{ color: 'white', fontSize: '0.88rem' }}>
                      <span style={{ color: 'rgba(255,255,255,0.7)' }}>Hello, </span>
                      <span className="fw-semibold">{currentUser?.name}</span>
                    </span>
                  }
                  id="user-dropdown"
                  align="end"
                  style={{ fontSize: '0.88rem' }}
                  menuVariant="dark"
                >
                  <NavDropdown.Item 
                    onClick={handleLogout}
                    style={{ fontSize: '0.9rem' }}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/login">Login</Nav.Link>
              </>
            )}
          </Nav>
        </div>
      </Container>
    </Navbar>
  );
};

export default AppNavbar;
